import {useCallback, useMemo, useRef} from 'react'
import {SelectPanel, SkeletonBox} from '@primer/react'
import {PlusIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {newRepoPath} from '@github-ui/paths'
import {sendEvent} from '@github-ui/hydro-analytics'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {useChatStateValue} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {queryForRepos} from '@github-ui/copilot-chat/components/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat/utils/copilot-chat-types'

import {EnvironmentPicker} from './EnvironmentPicker'
import type {EnvironmentOption} from './environment-option'
import {RepoBranchPill} from './RepoBranchPill'
import styles from './EnvironmentStrip.module.css'

export interface EnvironmentStripProps {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  selectedRepo: AgentDropdownRepository | undefined
  selectedBranch: string | undefined
  onRepoSelect: (repo: AgentDropdownRepository | undefined) => void
  onBranchSelect: (branch: string | undefined) => void
  currentPrompt?: string
  selectedEnvironment: EnvironmentOption | null
  onSelectEnvironment: (env: EnvironmentOption) => void
  onSelectedEnvironmentUnavailable?: () => void
}

export function EnvironmentStrip({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  selectedRepo,
  selectedBranch,
  onRepoSelect,
  onBranchSelect,
  currentPrompt,
  selectedEnvironment,
  onSelectEnvironment,
  onSelectedEnvironmentUnavailable,
}: EnvironmentStripProps) {
  const hasAutoSelectedRef = useRef(false)
  const currentUserLogin = useChatStateValue('currentUserLogin')

  const onLoad = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      if (!selectedRepo && !hasAutoSelectedRef.current) {
        let initialRepo: AgentDropdownRepository | undefined
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo && initialRepo.enabled) {
            onRepoSelect(initialRepo)
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo && loadedLastUsedRepo.enabled) {
            onRepoSelect(loadedLastUsedRepo)
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onRepoSelect(firstEnabledRepo)
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [selectedRepo, initialRepoNWO, onRepoSelect, onBranchSelect, initialBranch],
  )

  const repositoryLoader = useCallback(
    (query: string, defaultOwner?: string) => queryForRepos(query, defaultOwner, initialRepoNWO),
    [initialRepoNWO],
  )

  const newRepoUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (currentUserLogin) {
      params.set('owner', currentUserLogin)
    }
    if (currentPrompt?.trim()) {
      params.set('prompt', currentPrompt.trim())
    }
    const query = params.toString()
    return query ? `${newRepoPath()}?${query}` : newRepoPath()
  }, [currentUserLogin, currentPrompt])

  const isLocalDevice = selectedEnvironment?.kind === 'local'
  const hasPrimaryRepo = !!selectedRepo

  const newRepoLink = (
    <SelectPanel.SecondaryActionLink
      href={newRepoUrl}
      leadingVisual={PlusIcon}
      onClick={() => {
        sendEvent('agent_sessions.task_view', {target: 'NEW_REPOSITORY_LINK'})
      }}
    >
      New repository
    </SelectPanel.SecondaryActionLink>
  )

  return (
    <div className={styles.strip}>
      <EnvironmentPicker
        selectedEnvironmentId={selectedEnvironment?.id}
        onSelectEnvironment={onSelectEnvironment}
        onSelectedEnvironmentUnavailable={onSelectedEnvironmentUnavailable}
      />

      {!isLocalDevice && (
        <>
          {!hideRepoSelector && !hasPrimaryRepo && (
            <ReposSelector<AgentDropdownRepository>
              repositoryLoader={repositoryLoader}
              selectionVariant="single"
              iconOnly={false}
              responsiveStyle="full"
              selectAllOption={false}
              onSelect={repo => {
                if (repo) {
                  onRepoSelect(repo)
                  onBranchSelect(repo.defaultBranch)
                }
              }}
              currentSelection={selectedRepo}
              width="medium"
              additionalButtonProps={{
                'aria-label': 'Select repository',
                className: styles.stripButton,
                variant: 'invisible',
                size: 'small',
              }}
              onLoad={onLoad}
              loadingElement={<SkeletonBox width="120px" height="20px" />}
              secondaryAction={newRepoLink}
            />
          )}

          {hasPrimaryRepo && (
            <RepoBranchPill
              pair={{id: 'primary', repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}}
              onRepoChange={(_id, repo) => {
                onRepoSelect(repo)
                onBranchSelect(repo.defaultBranch)
              }}
              onBranchChange={(_id, branch) => onBranchSelect(branch)}
              onRemove={() => {
                onRepoSelect(undefined)
                onBranchSelect(undefined)
              }}
              removable={false}
              hideRepo={hideRepoSelector}
              initialRepoNWO={initialRepoNWO}
              onLoad={onLoad}
              secondaryAction={newRepoLink}
            />
          )}
        </>
      )}
    </div>
  )
}
